import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../context/AuthContext';
import { 
  ClipboardCheck, Plus, Edit, Trash2, Loader2, 
  BookOpen, CheckCircle, Bell, Clock, Users,
  TrendingUp, AlertTriangle, Award, ChevronDown, ChevronUp,
  UserX, UserCheck, Clock3, UserCog, Search
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminChecklistPage() {
  const { api } = useAuth();
  const [courseItems, setCourseItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [instructorStats, setInstructorStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('course');
  const [expandedInstructor, setExpandedInstructor] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    item_type: 'pre_class',
    is_optional: false,
    order: 0,
    course: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [courseRes, coursesRes, notifRes, statsRes] = await Promise.all([
        api.get('/checklists/course/'),
        api.get('/courses/'),
        api.get('/admin/checklist-notifications/'),
        api.get('/admin/instructor-checklist-stats/')
      ]);
      
      setCourseItems(courseRes.data.results || courseRes.data || []);
      setCourses(coursesRes.data.results || coursesRes.data || []);
      setNotifications(notifRes.data);
      setInstructorStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load checklist data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      item_type: 'pre_class',
      is_optional: false,
      order: 0,
      course: '',
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null, forCourseId = null) => {
    if (item) {
      setEditingItem({ ...item, type: 'course' });
      setFormData({
        name: item.name || '',
        description: item.description || '',
        item_type: item.item_type || 'pre_class',
        is_optional: item.is_optional || false,
        order: item.order || 0,
        course: item.course || forCourseId || '',
      });
    } else {
      resetForm();
      if (forCourseId) {
        setFormData(prev => ({ ...prev, course: forCourseId }));
      }
      setEditingItem({ type: 'course' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter an item name');
      return;
    }
    if (!formData.course) {
      toast.error('Please select a course');
      return;
    }

    setSaving(true);
    try {
      const endpoint = '/checklists/course/';
      const payload = {
        name: formData.name,
        description: formData.description || null,
        item_type: formData.item_type,
        is_optional: formData.is_optional,
        order: formData.order,
        course: formData.course,
      };

      if (editingItem?.id) {
        await api.put(`${endpoint}${editingItem.id}/`, payload);
        toast.success('Item updated successfully!');
      } else {
        await api.post(endpoint, payload);
        toast.success('Item created successfully!');
      }
      
      await fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Save error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;
    
    try {
      await api.delete(`/checklists/course/${item.id}/`);
      toast.success('Item deleted');
      await fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Stats
  const totalCourseItems = courseItems.length;
  const coursesWithChecklists = new Set(courseItems.map(i => i.course)).size;
  const recentCompletions = notifications.recent_completions?.length || 0;
  const pendingToday = notifications.pending_today || 0;

  // Filter courses by search
  const filteredCourses = courses.filter(c =>
    c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.category || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.subcategory || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get checklist items for a specific course
  const getItemsForCourse = (courseId) => courseItems.filter(i => i.course === courseId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6" />
            Checklist Management
          </h1>
          <p className="text-muted-foreground">Manage pre-class and final session checklists for each curriculum</p>
        </div>
        {selectedCourse && (
          <Button onClick={() => handleOpenDialog(null, selectedCourse.id)} data-testid="add-checklist-item-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{courses.length}</p>
                <p className="text-sm text-muted-foreground">Total Curriculum</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{coursesWithChecklists}</p>
                <p className="text-sm text-muted-foreground">With Checklists</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Bell className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{recentCompletions}</p>
                <p className="text-sm text-muted-foreground">Recent Completions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{pendingToday}</p>
                <p className="text-sm text-muted-foreground">Pending Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v !== 'course') setSelectedCourse(null); }}>
        <TabsList>
          <TabsTrigger value="course" className="flex items-center gap-2" data-testid="tab-curriculum">
            <BookOpen className="h-4 w-4" />
            Curriculum
          </TabsTrigger>
          <TabsTrigger value="instructor-stats" className="flex items-center gap-2" data-testid="tab-instructor-stats">
            <Users className="h-4 w-4" />
            Instructor Stats
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2" data-testid="tab-notifications">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Curriculum / Course-Specific Items */}
        <TabsContent value="course">
          {selectedCourse ? (
            /* Course detail view — show checklist items for selected course */
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => setSelectedCourse(null)} data-testid="back-to-curriculum-btn">
                      <ChevronUp className="h-4 w-4 mr-1 -rotate-90" />
                      Back to Curriculum
                    </Button>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      {selectedCourse.title}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedCourse.category}{selectedCourse.subcategory ? ` / ${selectedCourse.subcategory}` : ''}
                    </p>
                  </div>
                  <Button onClick={() => handleOpenDialog(null, selectedCourse.id)} data-testid="add-course-checklist-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Checklist Item
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {getItemsForCourse(selectedCourse.id).length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No checklist items yet</p>
                    <p className="text-sm mt-1">Create checklist items for instructors to complete before or during class</p>
                    <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog(null, selectedCourse.id)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Item
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Pre-Class Items */}
                    {getItemsForCourse(selectedCourse.id).filter(i => i.item_type === 'pre_class').length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">Pre-Class Items</h4>
                        <div className="space-y-2">
                          {getItemsForCourse(selectedCourse.id).filter(i => i.item_type === 'pre_class').map(item => (
                            <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`checklist-item-${item.id}`}>
                              <div className="flex items-center gap-3">
                                <span className="font-medium">{item.name}</span>
                                {item.is_optional && <Badge variant="outline">Optional</Badge>}
                                {!item.active && <Badge variant="destructive">Inactive</Badge>}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item, selectedCourse.id)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(item)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Final Session Items */}
                    {getItemsForCourse(selectedCourse.id).filter(i => i.item_type === 'final_session').length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">Final Session Items</h4>
                        <div className="space-y-2">
                          {getItemsForCourse(selectedCourse.id).filter(i => i.item_type === 'final_session').map(item => (
                            <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg bg-purple-50/50" data-testid={`checklist-item-${item.id}`}>
                              <div className="flex items-center gap-3">
                                <span className="font-medium">{item.name}</span>
                                {item.is_optional && <Badge variant="outline">Optional</Badge>}
                                {!item.active && <Badge variant="destructive">Inactive</Badge>}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item, selectedCourse.id)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(item)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Curriculum list — browse all courses */
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Curriculum</CardTitle>
                    <p className="text-sm text-muted-foreground">Select a curriculum to manage its checklist items</p>
                  </div>
                  <div className="w-72">
                    <Input
                      placeholder="Search curriculum..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      data-testid="curriculum-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredCourses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No curriculum found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredCourses.map(course => {
                      const itemCount = getItemsForCourse(course.id).length;
                      return (
                        <div
                          key={course.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => setSelectedCourse(course)}
                          data-testid={`curriculum-row-${course.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <BookOpen className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{course.title}</p>
                              <p className="text-sm text-muted-foreground">
                                {course.category}{course.subcategory ? ` / ${course.subcategory}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {itemCount > 0 ? (
                              <Badge variant="secondary" data-testid={`item-count-${course.id}`}>
                                {itemCount} item{itemCount !== 1 ? 's' : ''}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">No items</Badge>
                            )}
                            <ChevronDown className="h-4 w-4 text-muted-foreground -rotate-90" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Instructor Stats */}
        <TabsContent value="instructor-stats">
          <div className="space-y-6">
            {/* Summary Cards */}
            {instructorStats?.summary && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-8 w-8 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{instructorStats.summary.total_instructors}</p>
                        <p className="text-sm text-muted-foreground">Instructors</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <Award className="h-8 w-8 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{instructorStats.summary.instructors_excellent}</p>
                        <p className="text-sm text-muted-foreground">Excellent</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-8 w-8 text-amber-500" />
                      <div>
                        <p className="text-2xl font-bold">{instructorStats.summary.instructors_at_risk}</p>
                        <p className="text-sm text-muted-foreground">At Risk</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-8 w-8 text-purple-500" />
                      <div>
                        <p className="text-2xl font-bold">{instructorStats.summary.avg_completion_rate}%</p>
                        <p className="text-sm text-muted-foreground">Checklist Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <UserCheck className="h-8 w-8 text-teal-500" />
                      <div>
                        <p className="text-2xl font-bold">{instructorStats.summary.avg_attendance_rate || 0}%</p>
                        <p className="text-sm text-muted-foreground">Attendance Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className={instructorStats.summary.total_absences > 0 ? 'border-red-200 bg-red-50' : ''}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <UserX className="h-8 w-8 text-red-500" />
                      <div>
                        <p className="text-2xl font-bold">{instructorStats.summary.total_absences || 0}</p>
                        <p className="text-sm text-muted-foreground">Total Absences</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Instructor List */}
            <Card>
              <CardHeader>
                <CardTitle>Instructor Reliability Dashboard</CardTitle>
                <p className="text-sm text-muted-foreground">Track checklist completion and attendance to identify training needs or reliability issues</p>
              </CardHeader>
              <CardContent>
                {!instructorStats?.instructor_stats?.length ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No instructor data available yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {instructorStats.instructor_stats.map((instructor) => {
                      const isExpanded = expandedInstructor === instructor.instructor_id;
                      const statusColors = {
                        excellent: 'bg-green-100 text-green-800 border-green-200',
                        good: 'bg-blue-100 text-blue-800 border-blue-200',
                        needs_improvement: 'bg-amber-100 text-amber-800 border-amber-200',
                        at_risk: 'bg-red-100 text-red-800 border-red-200',
                        no_data: 'bg-gray-100 text-gray-800 border-gray-200'
                      };
                      const statusLabels = {
                        excellent: 'Excellent',
                        good: 'Good',
                        needs_improvement: 'Needs Improvement',
                        at_risk: 'At Risk',
                        no_data: 'No Data'
                      };
                      const hasAbsences = instructor.sessions_absent > 0;

                      return (
                        <div 
                          key={instructor.instructor_id} 
                          className={`border rounded-lg overflow-hidden ${instructor.status_badge === 'at_risk' ? 'border-red-200 bg-red-50/30' : ''} ${hasAbsences ? 'ring-2 ring-red-200' : ''}`}
                        >
                          <div 
                            className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                            onClick={() => setExpandedInstructor(isExpanded ? null : instructor.instructor_id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasAbsences ? 'bg-red-100' : 'bg-primary/10'}`}>
                                  <span className={`font-bold ${hasAbsences ? 'text-red-600' : 'text-primary'}`}>
                                    {instructor.instructor_name.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium">{instructor.instructor_name}</p>
                                  <p className="text-sm text-muted-foreground">{instructor.instructor_email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-6">
                                {/* Attendance Stats */}
                                <div className="flex items-center gap-4 text-sm">
                                  <div className="flex items-center gap-1" title="Present">
                                    <UserCheck className="h-4 w-4 text-green-600" />
                                    <span>{instructor.sessions_present || 0}</span>
                                  </div>
                                  {instructor.sessions_late > 0 && (
                                    <div className="flex items-center gap-1" title="Late">
                                      <Clock3 className="h-4 w-4 text-amber-600" />
                                      <span>{instructor.sessions_late}</span>
                                    </div>
                                  )}
                                  {instructor.sessions_absent > 0 && (
                                    <div className="flex items-center gap-1 text-red-600 font-bold" title="Absent (Called Off)">
                                      <UserX className="h-4 w-4" />
                                      <span>{instructor.sessions_absent}</span>
                                    </div>
                                  )}
                                  {instructor.sessions_substitute > 0 && (
                                    <div className="flex items-center gap-1" title="Sent Substitute">
                                      <UserCog className="h-4 w-4 text-blue-600" />
                                      <span>{instructor.sessions_substitute}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold">{instructor.attendance_rate || 0}%</p>
                                  <p className="text-xs text-muted-foreground">attendance</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold">{instructor.completion_rate}%</p>
                                  <p className="text-xs text-muted-foreground">checklists</p>
                                </div>
                                <Badge className={statusColors[instructor.status_badge]}>
                                  {statusLabels[instructor.status_badge]}
                                </Badge>
                                {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                              </div>
                            </div>
                            
                            {/* Progress Bars */}
                            <div className="mt-3 grid grid-cols-2 gap-4">
                              <div>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-muted-foreground">Attendance</span>
                                  <span>{instructor.attendance_rate || 0}%</span>
                                </div>
                                <Progress value={instructor.attendance_rate || 0} className="h-2" />
                              </div>
                              <div>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-muted-foreground">Checklists</span>
                                  <span>{instructor.completion_rate}%</span>
                                </div>
                                <Progress value={instructor.completion_rate} className="h-2" />
                              </div>
                            </div>
                          </div>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t bg-muted/30">
                              <div className="grid grid-cols-4 gap-4 mt-4">
                                <div>
                                  <p className="text-sm text-muted-foreground">Total Sessions</p>
                                  <p className="font-bold">{instructor.total_sessions}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Checklists Completed</p>
                                  <p className="font-bold">{instructor.completed_checklists} ({instructor.on_time_rate}% on-time)</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Sessions Present</p>
                                  <p className="font-bold text-green-600">{instructor.sessions_present || 0}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Sessions Absent</p>
                                  <p className={`font-bold ${instructor.sessions_absent > 0 ? 'text-red-600' : ''}`}>{instructor.sessions_absent || 0}</p>
                                </div>
                              </div>
                              
                              {instructor.recent_activity?.length > 0 && (
                                <div className="mt-4">
                                  <p className="text-sm font-medium mb-2">Recent Sessions</p>
                                  <div className="space-y-2">
                                    {instructor.recent_activity.map((activity, idx) => {
                                      const attendanceIcons = {
                                        present: <UserCheck className="h-4 w-4 text-green-500" />,
                                        absent: <UserX className="h-4 w-4 text-red-500" />,
                                        late: <Clock3 className="h-4 w-4 text-amber-500" />,
                                        substitute: <UserCog className="h-4 w-4 text-blue-500" />,
                                        pending: <Clock className="h-4 w-4 text-gray-400" />
                                      };
                                      const attendanceLabels = {
                                        present: 'Present',
                                        absent: 'Absent',
                                        late: 'Late',
                                        substitute: 'Substitute',
                                        pending: 'Pending'
                                      };
                                      return (
                                        <div key={idx} className={`flex items-center justify-between text-sm p-2 bg-white rounded border ${activity.attendance_status === 'absent' ? 'border-red-200 bg-red-50' : ''}`}>
                                          <div className="flex items-center gap-2">
                                            {attendanceIcons[activity.attendance_status] || attendanceIcons.pending}
                                            <span>{activity.course_title}</span>
                                            <Badge variant="outline" className="text-xs">Session {activity.session_number}</Badge>
                                          </div>
                                          <div className="flex items-center gap-2 text-muted-foreground">
                                            <span>{new Date(activity.session_date).toLocaleDateString()}</span>
                                            <Badge variant={activity.attendance_status === 'absent' ? 'destructive' : 'outline'} className="text-xs">
                                              {attendanceLabels[activity.attendance_status] || 'Pending'}
                                            </Badge>
                                            {activity.is_completed && (
                                              <Badge variant="outline" className="text-xs text-green-600">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                Checklist Done
                                              </Badge>
                                            )}
                                            {activity.attendance_notes && (
                                              <span className="text-xs italic" title={activity.attendance_notes}>📝</span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Recent Checklist Completions</CardTitle>
              <p className="text-sm text-muted-foreground">Notifications from instructors completing their checklists</p>
            </CardHeader>
            <CardContent>
              {(notifications.recent_completions?.length || 0) === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No recent completions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.recent_completions?.map((completion, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="font-medium">{completion.course_title}</span>
                          <Badge variant="secondary">Session {completion.session_number}</Badge>
                          {completion.is_final_session && (
                            <Badge variant="outline" className="bg-purple-50">Final</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Completed by {completion.completed_by} • {new Date(completion.completed_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem?.id ? 'Edit Checklist Item' : 'Add Checklist Item'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Pick up Laptops"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.item_type} onValueChange={(v) => setFormData({ ...formData, item_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre_class">Pre-Class</SelectItem>
                    <SelectItem value="final_session">Final Session</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            {editingItem?.type === 'course' && (
              <div className="space-y-2">
                <Label>Curriculum *</Label>
                <Select value={formData.course} onValueChange={(v) => setFormData({ ...formData, course: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select curriculum" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="is_optional"
                checked={formData.is_optional}
                onCheckedChange={(checked) => setFormData({ ...formData, is_optional: checked })}
              />
              <Label htmlFor="is_optional">Optional item (shows "if needed" suffix)</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingItem?.id ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
